import { useTranslation } from "react-i18next";

import { FilterBar } from "@/components/filter-bar";
import { MultiSelect } from "@/components/multi-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ConnectorToolGroup } from "@/lib/api";

import {
  ACCESS_FILTERS,
  type AccessFilter,
  chosenGroups,
  groupsFilterLabel,
  hasToolFilters,
  type KindFilter,
  type ToolFilters,
  type ToolKind,
} from "./utils";

interface ToolFiltersBarProps {
  filters: ToolFilters;
  onChange: (filters: ToolFilters) => void;
  /** Clears every filter, the search included. */
  onReset: () => void;
  /** The groups the viewer's page lists (`listedGroups`), in the dashboard's language. */
  groups: readonly ConnectorToolGroup[];
  /** The kinds the viewer's page lists (`listedKinds`). */
  kinds: readonly ToolKind[];
}

/**
 * One row of filters above the tools (`<FilterBar>`): a search (on title,
 * summary and technical name, `toolSearchTexts`); read or write; the kind
 * (Odoo, the account, the ideas and questions for Velta, the tools of a
 * role); the groups; and a reset once any of them is set.
 */
export function ToolFiltersBar({ filters, onChange, onReset, groups, kinds }: ToolFiltersBarProps) {
  const { t } = useTranslation();
  const set = (change: Partial<ToolFilters>) => onChange({ ...filters, ...change });

  return (
    <FilterBar
      search={{
        value: filters.search,
        onChange: (search) => set({ search }),
        label: t("tools.filter.search"),
      }}
      columns={["narrow", "medium", "wide"]}
      filtered={hasToolFilters(filters)}
      onReset={onReset}
    >
      <Select
        value={filters.access}
        onValueChange={(access) => set({ access: access as AccessFilter })}
      >
        <SelectTrigger size="sm" aria-label={t("tools.details.access")} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {ACCESS_FILTERS.map((access) => (
            <SelectItem key={access} value={access}>
              {access === "all" ? t("tools.filter.allAccess") : t(`tools.access.${access}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={filters.kind} onValueChange={(kind) => set({ kind: kind as KindFilter })}>
        <SelectTrigger size="sm" aria-label={t("tools.filter.kind")} className="w-full">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t("tools.filter.allKinds")}</SelectItem>
          {kinds.map((kind) => (
            <SelectItem key={kind} value={kind}>
              {t(`tools.kind.${kind}`)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <MultiSelect
        size="sm"
        className="w-full"
        options={groups.map((group) => ({ value: group.id, label: group.title }))}
        value={filters.groups}
        onChange={(chosen) => set({ groups: chosenGroups(chosen, groups) })}
        label={groupsFilterLabel(filters.groups, groups)}
        name={t("tools.filter.group")}
        allLabel={t("tools.filter.allGroups")}
      />
    </FilterBar>
  );
}
